import {
  type FC,
  type TransitionEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";
import { MemCommonMarkdown } from "@mem-labs/common-editor";
import { FloatingAgentWindowAnchor } from "@/modules/electron/types";
import { MDSIconButton, MDSIconButtonSize, MDSIconButtonVariant } from "@/mds/components/buttons";
import { MDSIconKind } from "@/mds/components/icon";

const BUBBLE_RADIUS_PX = 28;
const MAX_CONTENT_LINES = 4;
const BUBBLE_EXPANSION_DURATION_MS = 300;
const BUBBLE_EXPANSION_EASING = "cubic-bezier(0.2, 0.85, 0.25, 1)";
const BUBBLE_EXPANSION_BLUR_PX = 0.35;
const REDUCED_MOTION_MEDIA_QUERY = "(prefers-reduced-motion: reduce)";

const prefersReducedMotion = (): boolean =>
  window.matchMedia?.(REDUCED_MOTION_MEDIA_QUERY).matches ?? false;

/**
 * Padding and borders the bubble wraps around its body, measured off the
 * element rather than restated as a constant — the padding comes from theme
 * spacing, so a hardcoded copy silently goes stale when the token changes.
 */
const measureBubbleVerticalChromePx = (bubble: HTMLElement): number => {
  const style = getComputedStyle(bubble);
  return (
    parseFloat(style.paddingTop) +
    parseFloat(style.paddingBottom) +
    parseFloat(style.borderTopWidth) +
    parseFloat(style.borderBottomWidth)
  );
};

/**
 * Preview bubble ("peek"): an agent reply arriving while only the circle is
 * showing pops out of the floater, styled like a chat message bubble with the
 * corner nearest the circle squared. Persists until interaction — click opens
 * the panel at the message, the hover-revealed × dismisses.
 *
 * Hovering expands the clamped preview to the whole message. When the message
 * is taller than `maxExpandedHeightPx` the body stops growing and scrolls
 * instead, so a long reply can never outgrow the screen or the tab.
 */
export const FloatingAgentPreviewBubble: FC<{
  content: string;
  /** Stable reply identity used to ignore a previous bubble's delayed collapse. */
  expansionKey: string;
  anchor: FloatingAgentWindowAnchor;
  /** Only the bubble adjacent to the circle squares its circle-facing corner. */
  showTail: boolean;
  ariaLabel?: string;
  canDismiss?: boolean;
  /**
   * Ceiling for the hover-expanded bubble, in px. The host owns this because
   * only it knows what room is left: the desktop host subtracts the circle and
   * the sibling bubbles from the display work area, the web host from the
   * viewport. Omitted means unconstrained.
   */
  maxExpandedHeightPx?: number;
  /**
   * Reports this bubble's stable identity and desired hovered height, then
   * `null` once its collapse settles. The desktop host needs both so a stale
   * collapse from one reply cannot shrink the window under another reply.
   */
  onExpandedHeightChange?: (expansionKey: string, heightPx: number | null) => void;
  onOpen: () => void;
  onDismiss?: () => void;
}> = ({
  content,
  expansionKey,
  anchor,
  showTail,
  ariaLabel = "New message from Mem",
  canDismiss = true,
  maxExpandedHeightPx,
  onExpandedHeightChange,
  onOpen,
  onDismiss,
}) => {
  /* The whole bubble is one click target, so a mem link inside the preview
     opens the panel rather than navigating the floater to the item. */
  const handleClickMemItem = useCallback(() => {
    onOpen();
  }, [onOpen]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isCollapseSettled, setIsCollapseSettled] = useState(true);
  const [isBodyAnimating, setIsBodyAnimating] = useState(false);
  const [collapsedBodyHeightPx, setCollapsedBodyHeightPx] = useState<number | null>(null);
  const [expandedBodyHeightPx, setExpandedBodyHeightPx] = useState<number | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const collapseTimerRef = useRef<number | null>(null);
  const bodyAnimationTimerRef = useRef<number | null>(null);

  const clearCollapseTimer = useCallback(() => {
    if (collapseTimerRef.current === null) return;

    window.clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = null;
  }, []);

  const clearBodyAnimationTimer = useCallback(() => {
    if (bodyAnimationTimerRef.current === null) return;

    window.clearTimeout(bodyAnimationTimerRef.current);
    bodyAnimationTimerRef.current = null;
  }, []);

  const completeBodyAnimation = useCallback(() => {
    clearBodyAnimationTimer();
    setIsBodyAnimating(false);
  }, [clearBodyAnimationTimer]);

  const beginBodyAnimation = useCallback(() => {
    clearBodyAnimationTimer();

    if (prefersReducedMotion()) {
      setIsBodyAnimating(false);
      return;
    }

    setIsBodyAnimating(true);
    bodyAnimationTimerRef.current = window.setTimeout(
      completeBodyAnimation,
      BUBBLE_EXPANSION_DURATION_MS
    );
  }, [clearBodyAnimationTimer, completeBodyAnimation]);

  const completeCollapse = useCallback(() => {
    clearCollapseTimer();
    completeBodyAnimation();
    setIsCollapseSettled(true);
    setExpandedBodyHeightPx(null);
    onExpandedHeightChange?.(expansionKey, null);
  }, [clearCollapseTimer, completeBodyAnimation, expansionKey, onExpandedHeightChange]);

  const handleExpand = useCallback(() => {
    clearCollapseTimer();
    beginBodyAnimation();
    setIsCollapseSettled(false);
    setIsExpanded(true);
  }, [beginBodyAnimation, clearCollapseTimer]);

  const handleCollapse = useCallback(() => {
    clearCollapseTimer();
    beginBodyAnimation();
    setIsCollapseSettled(false);
    setIsExpanded(false);

    if (prefersReducedMotion()) {
      completeCollapse();
      return;
    }

    /* Keep the native window at its expanded size until the visual collapse
       finishes. Otherwise Electron clips the top of the shrinking bubble. */
    collapseTimerRef.current = window.setTimeout(completeCollapse, BUBBLE_EXPANSION_DURATION_MS);
  }, [beginBodyAnimation, clearCollapseTimer, completeCollapse]);

  const handleBodyTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget || event.propertyName !== "max-height") return;
      if (isExpanded) {
        completeBodyAnimation();
        return;
      }

      completeCollapse();
    },
    [completeBodyAnimation, completeCollapse, isExpanded]
  );

  const measureCollapsedBody = useCallback(() => {
    const body = bodyRef.current;
    const markdown = body?.querySelector<HTMLElement>(".markdown");
    if (!body || !markdown) return;

    /* offsetHeight reports layout dimensions before transforms, unlike
       getBoundingClientRect(). The bubble's entrance animation begins at
       scale(0.7), so measuring visual coordinates here would permanently
       undersize the first collapsed preview. */
    const measuredHeightPx = markdown.offsetHeight;
    setCollapsedBodyHeightPx(currentHeightPx =>
      currentHeightPx === measuredHeightPx ? currentHeightPx : measuredHeightPx
    );
  }, []);

  const measureExpandedBody = useCallback(() => {
    const bubble = bubbleRef.current;
    const body = bodyRef.current;
    if (!bubble || !body) return;

    // scrollHeight is the unclamped content height even while max-height is
    // capping the visible box; the chrome around it is measured rather than
    // assumed so this cannot drift from the padding the stylesheet applies.
    const bubbleVerticalChromePx = measureBubbleVerticalChromePx(bubble);
    const requestedHeightPx = body.scrollHeight + bubbleVerticalChromePx;
    const cappedHeightPx =
      maxExpandedHeightPx === undefined
        ? requestedHeightPx
        : Math.min(requestedHeightPx, maxExpandedHeightPx);

    setExpandedBodyHeightPx(Math.max(cappedHeightPx - bubbleVerticalChromePx, 0));
    onExpandedHeightChange?.(expansionKey, cappedHeightPx);
  }, [expansionKey, maxExpandedHeightPx, onExpandedHeightChange]);

  /* Preserve the production clamp as the source of truth, then record the
     exact rendered height as the animation's collapsed endpoint. Measuring
     the clamped markdown avoids guessing from a parent line-height that may
     differ from Common Editor's typography. */
  useLayoutEffect(() => {
    if (isExpanded || !isCollapseSettled) return;

    measureCollapsedBody();
  }, [content, isCollapseSettled, isExpanded, measureCollapsedBody]);

  /* Measured before paint: the host resizes its OS window from this number, so
     reporting it a frame late would show one clipped frame of the expansion. */
  useLayoutEffect(() => {
    if (!isExpanded) return;

    measureExpandedBody();
  }, [content, isExpanded, measureExpandedBody]);

  /* The web widget can narrow without changing the reply. Observe the inner
     markdown so wrapping changes refresh whichever animation endpoint is
     active; observing the transitioning body itself would create a resize
     callback on every animation frame. */
  useEffect(() => {
    const markdown = bodyRef.current?.querySelector<HTMLElement>(".markdown");
    if (!markdown) return;

    const resizeObserver = new ResizeObserver(() => {
      if (isExpanded) {
        measureExpandedBody();
        return;
      }

      if (isCollapseSettled) measureCollapsedBody();
    });
    resizeObserver.observe(markdown);

    return () => resizeObserver.disconnect();
  }, [isCollapseSettled, isExpanded, measureCollapsedBody, measureExpandedBody]);

  /* An unmount mid-hover (dismissed, or the queue drained) would otherwise
     strand the host holding this bubble's expanded height forever. */
  useEffect(
    () => () => {
      clearCollapseTimer();
      clearBodyAnimationTimer();
      onExpandedHeightChange?.(expansionKey, null);
    },
    [clearBodyAnimationTimer, clearCollapseTimer, expansionKey, onExpandedHeightChange]
  );

  const isTopAnchored =
    anchor === FloatingAgentWindowAnchor.TopLeft || anchor === FloatingAgentWindowAnchor.TopRight;
  const isLeftAnchored =
    anchor === FloatingAgentWindowAnchor.TopLeft || anchor === FloatingAgentWindowAnchor.BottomLeft;

  return (
    <StyledBubble
      ref={bubbleRef}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      data-floating-agent-hit-target="true"
      onClick={onOpen}
      onMouseEnter={handleExpand}
      onMouseLeave={handleCollapse}
      onFocus={handleExpand}
      onBlur={handleCollapse}
      $isTopAnchored={isTopAnchored}
      $isLeftAnchored={isLeftAnchored}
      $squareCircleCorner={showTail}
      $isExpanded={isExpanded}
      $maxExpandedHeightPx={maxExpandedHeightPx}
    >
      <StyledBubbleBody
        ref={bodyRef}
        $isExpanded={isExpanded}
        $isClampApplied={!isExpanded && isCollapseSettled}
        $isAnimating={isBodyAnimating}
        $collapsedHeightPx={collapsedBodyHeightPx}
        $expandedHeightPx={expandedBodyHeightPx}
        onTransitionEnd={handleBodyTransitionEnd}
      >
        <MemCommonMarkdown
          unstyled
          contentAlignment="flex-start"
          onClickMemItem={handleClickMemItem}
        >
          {content}
        </MemCommonMarkdown>
      </StyledBubbleBody>
      {canDismiss && onDismiss && (
        <StyledDismissContainer className="bubble-dismiss">
          <MDSIconButton
            aria-label="Dismiss"
            iconKind={MDSIconKind.Exit}
            variant={MDSIconButtonVariant.Floating}
            size={MDSIconButtonSize.Small}
            rounded
            onClick={event => {
              event.stopPropagation();
              onDismiss();
            }}
          />
        </StyledDismissContainer>
      )}
    </StyledBubble>
  );
};

/* The circle sits inset in its window box ((96 − 64) / 2); the bubble's
   anchored edge gets the same inset so both align on one vertical line. */
const CIRCLE_EDGE_INSET_PX = 16;

const StyledBubble = styled.div<{
  $isTopAnchored: boolean;
  $isLeftAnchored: boolean;
  $squareCircleCorner: boolean;
  $isExpanded: boolean;
  $maxExpandedHeightPx: number | undefined;
}>`
  position: relative;
  width: 290px;
  /* Narrow phones (<338px viewport) would clip the fixed width. */
  max-width: calc(100vw - 48px);
  /* The expansion cap lives on the bubble, not its body: border-box means the
     browser subtracts this element's own padding and border, so the visible
     height matches the number reported to the host without restating them.
     Flex column so the capped bubble hands its leftover height to the body,
     which scrolls rather than overflowing. */
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  ${({ $isExpanded, $maxExpandedHeightPx }) =>
    $isExpanded && $maxExpandedHeightPx !== undefined && `max-height: ${$maxExpandedHeightPx}px;`}
  ${({ $isLeftAnchored }) =>
    $isLeftAnchored
      ? `margin-left: ${CIRCLE_EDGE_INSET_PX}px;`
      : `margin-right: ${CIRCLE_EDGE_INSET_PX}px;`}
  padding: ${({ theme }) => `${theme.spacing["4"]} ${theme.spacing["5"]}`};
  border-radius: ${BUBBLE_RADIUS_PX}px;
  /* The corner pointing at the circle is squared — the message-bubble
     convention standing in for the old speech-bubble tail. */
  ${({ $squareCircleCorner, $isTopAnchored, $isLeftAnchored }) =>
    $squareCircleCorner &&
    `border-${$isTopAnchored ? "top" : "bottom"}-${$isLeftAnchored ? "left" : "right"}-radius: 0;`}
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.background.message.user};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  cursor: pointer;
  pointer-events: auto;
  animation: floating-agent-bubble-enter 240ms cubic-bezier(0.16, 1, 0.3, 1) both;
  transform-origin: ${({ $isTopAnchored, $isLeftAnchored }) =>
    `${$isTopAnchored ? "top" : "bottom"} ${$isLeftAnchored ? "left" : "right"}`};

  @keyframes floating-agent-bubble-enter {
    from {
      opacity: 0;
      transform: scale(0.7);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  &:hover .bubble-dismiss,
  &:focus-within .bubble-dismiss {
    opacity: 1;
  }

  @media ${REDUCED_MOTION_MEDIA_QUERY} {
    animation: none;
    transition: none;
  }
`;

/* The markdown renderer nests a flex wrapper around a .markdown div, so the
   line clamp lives on that inner div — the only box that directly contains the
   rendered blocks. Block margins collapse to zero so a multi-block preview
   still clamps to MAX_CONTENT_LINES instead of spending lines on spacing, and
   typography is restated here because the renderer replaced MDSText.

   Expanded, the clamp is lifted and this box scrolls instead: the bubble's own
   max-height bounds it, so the message runs to its natural height and only
   scrolls once it would outgrow the room the host says is available. */
const StyledBubbleBody = styled.div<{
  $isExpanded: boolean;
  $isClampApplied: boolean;
  $isAnimating: boolean;
  $collapsedHeightPx: number | null;
  $expandedHeightPx: number | null;
}>`
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};
  /* The full message remains laid out behind this animated viewport. That
     lets max-height reveal whole lines smoothly in either direction; the
     line clamp comes back only after collapse so it cannot snap the height. */
  max-height: ${({ $isExpanded, $collapsedHeightPx, $expandedHeightPx }) => {
    if ($isExpanded && $expandedHeightPx !== null) return `${$expandedHeightPx}px`;
    if ($collapsedHeightPx !== null) return `${$collapsedHeightPx}px`;
    return "none";
  }};
  min-height: 0;
  overflow-y: ${({ $isExpanded, $expandedHeightPx }) =>
    $isExpanded && $expandedHeightPx !== null ? "auto" : "hidden"};
  overscroll-behavior: contain;
  filter: ${({ $isAnimating }) =>
    $isAnimating ? `blur(${BUBBLE_EXPANSION_BLUR_PX}px)` : "blur(0)"};
  transition:
    max-height ${BUBBLE_EXPANSION_DURATION_MS}ms ${BUBBLE_EXPANSION_EASING},
    filter 120ms ease-out;
  will-change: max-height, filter;

  [data-mem-common-markdown] {
    overflow-x: hidden;
  }

  .markdown {
    ${({ $isClampApplied }) =>
      $isClampApplied &&
      `
      display: -webkit-box;
      -webkit-line-clamp: ${MAX_CONTENT_LINES};
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
    overflow-wrap: break-word;
    width: 100%;
  }

  .markdown > * {
    margin-block: 0;
  }

  /* Headings would otherwise blow out the 290px bubble; the peek is a preview,
     so every block reads at the bubble's own size. */
  .markdown :is(h1, h2, h3, h4, h5, h6) {
    font-size: inherit;
    line-height: inherit;
  }

  .markdown :is(ul, ol) {
    padding-inline-start: ${({ theme }) => theme.spacing["5"]};
  }

  @media ${REDUCED_MOTION_MEDIA_QUERY} {
    filter: none;
    transition: none;
    will-change: auto;
  }
`;

/* Hover-revealed round dismiss straddling the bubble's top-right corner —
   the design centers the 32px button on the corner point (the reveal rule
   lives on StyledBubble). */
const StyledDismissContainer = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  opacity: 0;
  transition: opacity 120ms ease-out;

  @media ${REDUCED_MOTION_MEDIA_QUERY} {
    transition: none;
  }
`;
